import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AutoComplete,
  Button,
  Card,
  Checkbox,
  Col,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  Divider,
  message
} from 'antd';
import {
  CopyOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  TeamOutlined
} from '@ant-design/icons';
import { useLanguage } from '../contexts/LanguageContext';
import { commerceServerTablePagination, COMMERCE_DEFAULT_PAGE_SIZE } from '../constants/commerceListPagination';
import { getUserPrimaryCompanyId, hasRole } from '../utils/permissionUtils';
import {
  buildCustomerNameLocalesPayload,
  emptyCustomerNameLocalesForm,
  parseCustomerNameLocalesRoot,
  resolveCustomerDisplayName
} from '../constants/commerceNameLocales';
import { buildCommerceAuditColumns, useCommerceUserTimezone } from '../utils/commerceListDateUtils';

const { Title, Text } = Typography;

const CUSTOMER_STATUS_VALUES = ['Active', 'Suspended', 'Pending'];

const DAY_KEYS = ['delivery_day_mon', 'delivery_day_tue', 'delivery_day_wed', 'delivery_day_thu', 'delivery_day_fri', 'delivery_day_sat', 'delivery_day_sun'];
const DAY_I18N_KEYS = ['deliveryMon', 'deliveryTue', 'deliveryWed', 'deliveryThu', 'deliveryFri', 'deliverySat', 'deliverySun'];
const NAME_LINE_FORM_KEYS = [
  { line: '1', field: 'name_1' },
  { line: '2', field: 'name_2' },
  { line: '3', field: 'name_3' }
];
const pick = (obj, ...keys) => {
  for (const key of keys) {
    if (obj?.[key] !== undefined && obj?.[key] !== null) return obj[key];
  }
  return null;
};

const normalizeCustomerRow = (row) => {
  if (!row || typeof row !== 'object') return row;
  return {
    ...row,
    id: pick(row, 'id', 'Id'),
    customer_no: pick(row, 'customer_no', 'customerNo', 'CustomerNo'),
    branch_no: pick(row, 'branch_no', 'branchNo', 'BranchNo'),
    name_1: pick(row, 'name_1', 'name1', 'Name1', 'name_en_1', 'nameEn1', 'NameEn1'),
    name_2: pick(row, 'name_2', 'name2', 'Name2', 'name_en_2', 'nameEn2', 'NameEn2'),
    name_3: pick(row, 'name_3', 'name3', 'Name3', 'name_en_3', 'nameEn3', 'NameEn3'),
    name_locales: pick(row, 'name_locales', 'nameLocales', 'NameLocales'),
    billing_address_en_1: pick(row, 'billing_address_en_1', 'billingAddressEn1', 'BillingAddressEn1'),
    billing_address_en_2: pick(row, 'billing_address_en_2', 'billingAddressEn2', 'BillingAddressEn2'),
    billing_address_en_3: pick(row, 'billing_address_en_3', 'billingAddressEn3', 'BillingAddressEn3'),
    billing_address_cn_1: pick(row, 'billing_address_cn_1', 'billingAddressCn1', 'BillingAddressCn1'),
    billing_address_cn_2: pick(row, 'billing_address_cn_2', 'billingAddressCn2', 'BillingAddressCn2'),
    billing_address_cn_3: pick(row, 'billing_address_cn_3', 'billingAddressCn3', 'BillingAddressCn3'),
    delivery_address_en_1: pick(row, 'delivery_address_en_1', 'deliveryAddressEn1', 'DeliveryAddressEn1'),
    delivery_address_en_2: pick(row, 'delivery_address_en_2', 'deliveryAddressEn2', 'DeliveryAddressEn2'),
    delivery_address_en_3: pick(row, 'delivery_address_en_3', 'deliveryAddressEn3', 'DeliveryAddressEn3'),
    delivery_address_cn_1: pick(row, 'delivery_address_cn_1', 'deliveryAddressCn1', 'DeliveryAddressCn1'),
    delivery_address_cn_2: pick(row, 'delivery_address_cn_2', 'deliveryAddressCn2', 'DeliveryAddressCn2'),
    delivery_address_cn_3: pick(row, 'delivery_address_cn_3', 'deliveryAddressCn3', 'DeliveryAddressCn3'),
    contact_name: pick(row, 'contact_name', 'contactName', 'ContactName'),
    tel: pick(row, 'tel', 'Tel'),
    fax: pick(row, 'fax', 'Fax'),
    email: pick(row, 'email', 'Email'),
    whatsapp_no: pick(row, 'whatsapp_no', 'whatsappNo', 'WhatsappNo'),
    salesman_name: pick(row, 'salesman_name', 'salesmanName', 'SalesmanName'),
    salesman_code: pick(row, 'salesman_code', 'salesmanCode', 'SalesmanCode'),
    salesman_whatsapp: pick(row, 'salesman_whatsapp', 'salesmanWhatsapp', 'SalesmanWhatsapp'),
    delivery_day_mon: !!pick(row, 'delivery_day_mon', 'deliveryDayMon', 'DeliveryDayMon'),
    delivery_day_tue: !!pick(row, 'delivery_day_tue', 'deliveryDayTue', 'DeliveryDayTue'),
    delivery_day_wed: !!pick(row, 'delivery_day_wed', 'deliveryDayWed', 'DeliveryDayWed'),
    delivery_day_thu: !!pick(row, 'delivery_day_thu', 'deliveryDayThu', 'DeliveryDayThu'),
    delivery_day_fri: !!pick(row, 'delivery_day_fri', 'deliveryDayFri', 'DeliveryDayFri'),
    delivery_day_sat: !!pick(row, 'delivery_day_sat', 'deliveryDaySat', 'DeliveryDaySat'),
    delivery_day_sun: !!pick(row, 'delivery_day_sun', 'deliveryDaySun', 'DeliveryDaySun'),
    delivery_day_holiday: !!pick(row, 'delivery_day_holiday', 'deliveryDayHoliday', 'DeliveryDayHoliday'),
    credit_limit: pick(row, 'credit_limit', 'creditLimit', 'CreditLimit'),
    customer_status: (() => {
      const s = pick(row, 'customer_status', 'customerStatus', 'CustomerStatus');
      if (s == null || s === '') return 'Active';
      const t = String(s).trim();
      return CUSTOMER_STATUS_VALUES.includes(t) ? t : 'Active';
    })(),
    price_type: pick(row, 'price_type', 'priceType', 'PriceType'),
    payment_term: pick(row, 'payment_term', 'paymentTerm', 'PaymentTerm'),
    remark: pick(row, 'remark', 'Remark')
  };
};

const CommerceCustomersPage = () => {
  const { t, currentLanguage } = useLanguage();
  const userTimezoneOffset = useCommerceUserTimezone();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [customersTotal, setCustomersTotal] = useState(0);
  const [listPage, setListPage] = useState(1);
  const [listPageSize, setListPageSize] = useState(COMMERCE_DEFAULT_PAGE_SIZE);
  const [keyword, setKeyword] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [companyFilter, setCompanyFilter] = useState(() => getUserPrimaryCompanyId());
  const [companiesForSelect, setCompaniesForSelect] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [activeTab, setActiveTab] = useState('basic');
  const [form] = Form.useForm();

  const [paymentTermOptions, setPaymentTermOptions] = useState([
    'COD', '30日數期月結', '60日數期月結'
  ]);

  const [pwaModalOpen, setPwaModalOpen] = useState(false);
  const [pwaModalCustomer, setPwaModalCustomer] = useState(null);
  const [pwaModalAccounts, setPwaModalAccounts] = useState([]);
  const [pwaModalLoading, setPwaModalLoading] = useState(false);

  const userInfo = typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('userInfo') || '{}') : {};
  const isTenantAdmin = hasRole(userInfo, 'Tenant_Admin');

  const fetchWithAuth = useCallback((url, options = {}) => {
    const token = localStorage.getItem('token');
    const headers = { ...(options.headers || {}) };
    if (!headers['Content-Type']) {
      headers['Content-Type'] = 'application/json';
    }
    if (token) headers.Authorization = `Bearer ${token}`;
    headers['X-UI-Locale'] = currentLanguage || 'zh-TC';
    return fetch(url, { ...options, headers });
  }, [currentLanguage]);

  const extractResponseError = async (response) => {
    const text = await response.text();
    if (!text) return `HTTP ${response.status}`;
    try {
      const data = JSON.parse(text);
      return data?.error || data?.detail || data?.message || text;
    } catch {
      return text;
    }
  };

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (keyword.trim()) params.set('search', keyword.trim());
      if (companyFilter) params.set('companyId', companyFilter);
      params.set('page', String(listPage));
      params.set('pageSize', String(listPageSize));
      const response = await fetchWithAuth(`/api/commerce/customers?${params.toString()}`);
      if (!response.ok) throw new Error(await extractResponseError(response));
      const data = await response.json();
      const rawItems = Array.isArray(data?.items) ? data.items : [];
      const total = typeof data?.total === 'number' ? data.total : rawItems.length;
      setCustomers(rawItems.map(normalizeCustomerRow));
      setCustomersTotal(total);
    } catch (err) {
      message.error(`${t('commerceCustomers.loadFailed')}: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth, keyword, companyFilter, listPage, listPageSize, t]);

  const loadCompaniesForSelect = useCallback(async () => {
    try {
      const res = await fetchWithAuth('/api/commerce/companies-for-select');
      if (!res.ok) return;
      const data = await res.json();
      setCompaniesForSelect(Array.isArray(data) ? data : []);
    } catch (err) {
      console.warn('載入公司列表失敗:', err);
    }
  }, [fetchWithAuth]);

  const loadPaymentTerms = useCallback(async () => {
    try {
      const params = companyFilter ? `?companyId=${companyFilter}` : '';
      const res = await fetchWithAuth(`/api/commerce/payment-terms${params}`);
      if (!res.ok) return;
      const data = await res.json();
      if (Array.isArray(data) && data.length > 0) {
        const defaults = ['COD', '30日數期月結', '60日數期月結'];
        const merged = [...new Set([...defaults, ...data])];
        setPaymentTermOptions(merged);
      }
    } catch (err) {
      console.warn('載入付款條款失敗:', err);
    }
  }, [fetchWithAuth, companyFilter]);

  useEffect(() => {
    loadCustomers();
  }, [loadCustomers]);

  useEffect(() => {
    loadCompaniesForSelect();
    loadPaymentTerms();
  }, [loadCompaniesForSelect, loadPaymentTerms]);

  const openCreate = () => {
    setEditingRecord(null);
    setActiveTab('basic');
    form.resetFields();
    form.setFieldsValue({
      branch_no: '0000',
      customer_status: 'Active',
      credit_limit: null,
      payment_term: null,
      remark: null,
      nameLocales: emptyCustomerNameLocalesForm(),
      delivery_day_mon: false,
      delivery_day_tue: false,
      delivery_day_wed: false,
      delivery_day_thu: false,
      delivery_day_fri: false,
      delivery_day_sat: false,
      delivery_day_sun: false,
      delivery_day_holiday: false
    });
    setDrawerOpen(true);
  };

  const openEdit = (record) => {
    const row = normalizeCustomerRow(record);
    setEditingRecord(row);
    setActiveTab('basic');
    const nl = parseCustomerNameLocalesRoot(row.name_locales);
    form.setFieldsValue({
      customer_no: row.customer_no,
      branch_no: row.branch_no || '0000',
      name_1: row.name_1,
      name_2: row.name_2,
      name_3: row.name_3,
      nameLocales: nl,
      contact_name: row.contact_name,
      tel: row.tel,
      fax: row.fax,
      email: row.email,
      whatsapp_no: row.whatsapp_no,
      credit_limit: row.credit_limit != null && row.credit_limit !== '' ? Number(row.credit_limit) : null,
      customer_status: row.customer_status || 'Active',
      billing_address_en_1: row.billing_address_en_1,
      billing_address_en_2: row.billing_address_en_2,
      billing_address_en_3: row.billing_address_en_3,
      billing_address_cn_1: row.billing_address_cn_1,
      billing_address_cn_2: row.billing_address_cn_2,
      billing_address_cn_3: row.billing_address_cn_3,
      delivery_address_en_1: row.delivery_address_en_1,
      delivery_address_en_2: row.delivery_address_en_2,
      delivery_address_en_3: row.delivery_address_en_3,
      delivery_address_cn_1: row.delivery_address_cn_1,
      delivery_address_cn_2: row.delivery_address_cn_2,
      delivery_address_cn_3: row.delivery_address_cn_3,
      delivery_day_mon: !!row.delivery_day_mon,
      delivery_day_tue: !!row.delivery_day_tue,
      delivery_day_wed: !!row.delivery_day_wed,
      delivery_day_thu: !!row.delivery_day_thu,
      delivery_day_fri: !!row.delivery_day_fri,
      delivery_day_sat: !!row.delivery_day_sat,
      delivery_day_sun: !!row.delivery_day_sun,
      delivery_day_holiday: !!row.delivery_day_holiday,
      salesman_name: row.salesman_name,
      salesman_code: row.salesman_code,
      salesman_whatsapp: row.salesman_whatsapp,
      price_type: row.price_type,
      payment_term: row.payment_term || null,
      remark: row.remark || null
    });
    setDrawerOpen(true);
  };

  const openCopy = (record) => {
    const row = normalizeCustomerRow(record);
    setEditingRecord(null);
    setActiveTab('basic');
    form.resetFields();
    const nlCopy = parseCustomerNameLocalesRoot(row.name_locales);
    form.setFieldsValue({
      customer_no: '',
      branch_no: row.branch_no || '0000',
      name_1: row.name_1,
      name_2: row.name_2,
      name_3: row.name_3,
      nameLocales: nlCopy,
      contact_name: row.contact_name,
      tel: row.tel,
      fax: row.fax,
      email: row.email,
      whatsapp_no: row.whatsapp_no,
      credit_limit: row.credit_limit != null && row.credit_limit !== '' ? Number(row.credit_limit) : null,
      customer_status: row.customer_status || 'Active',
      billing_address_en_1: row.billing_address_en_1,
      billing_address_en_2: row.billing_address_en_2,
      billing_address_en_3: row.billing_address_en_3,
      billing_address_cn_1: row.billing_address_cn_1,
      billing_address_cn_2: row.billing_address_cn_2,
      billing_address_cn_3: row.billing_address_cn_3,
      delivery_address_en_1: row.delivery_address_en_1,
      delivery_address_en_2: row.delivery_address_en_2,
      delivery_address_en_3: row.delivery_address_en_3,
      delivery_address_cn_1: row.delivery_address_cn_1,
      delivery_address_cn_2: row.delivery_address_cn_2,
      delivery_address_cn_3: row.delivery_address_cn_3,
      delivery_day_mon: !!row.delivery_day_mon,
      delivery_day_tue: !!row.delivery_day_tue,
      delivery_day_wed: !!row.delivery_day_wed,
      delivery_day_thu: !!row.delivery_day_thu,
      delivery_day_fri: !!row.delivery_day_fri,
      delivery_day_sat: !!row.delivery_day_sat,
      delivery_day_sun: !!row.delivery_day_sun,
      delivery_day_holiday: !!row.delivery_day_holiday,
      salesman_name: row.salesman_name,
      salesman_code: row.salesman_code,
      salesman_whatsapp: row.salesman_whatsapp,
      price_type: row.price_type,
      payment_term: row.payment_term || null,
      remark: row.remark || null
    });
    setDrawerOpen(true);
  };

  const saveCustomer = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const isEdit = !!editingRecord;
      const url = isEdit ? `/api/commerce/customers/${editingRecord.id}` : '/api/commerce/customers';
      const method = isEdit ? 'PUT' : 'POST';
      const nlPayload = buildCustomerNameLocalesPayload(values.nameLocales);
      const payload = {
        customer_no: values.customer_no,
        branch_no: values.branch_no,
        name_1: values.name_1,
        name_2: values.name_2,
        name_3: values.name_3,
        ...(nlPayload ? { name_locales: nlPayload } : {}),
        billing_address_en_1: values.billing_address_en_1,
        billing_address_en_2: values.billing_address_en_2,
        billing_address_en_3: values.billing_address_en_3,
        billing_address_cn_1: values.billing_address_cn_1,
        billing_address_cn_2: values.billing_address_cn_2,
        billing_address_cn_3: values.billing_address_cn_3,
        delivery_address_en_1: values.delivery_address_en_1,
        delivery_address_en_2: values.delivery_address_en_2,
        delivery_address_en_3: values.delivery_address_en_3,
        delivery_address_cn_1: values.delivery_address_cn_1,
        delivery_address_cn_2: values.delivery_address_cn_2,
        delivery_address_cn_3: values.delivery_address_cn_3,
        contact_name: values.contact_name,
        tel: values.tel,
        fax: values.fax,
        email: values.email,
        whatsapp_no: values.whatsapp_no,
        salesman_name: values.salesman_name,
        salesman_code: values.salesman_code,
        salesman_whatsapp: values.salesman_whatsapp,
        credit_limit: values.credit_limit === undefined || values.credit_limit === null ? null : Number(values.credit_limit),
        customer_status: values.customer_status || 'Active',
        delivery_day_mon: !!values.delivery_day_mon,
        delivery_day_tue: !!values.delivery_day_tue,
        delivery_day_wed: !!values.delivery_day_wed,
        delivery_day_thu: !!values.delivery_day_thu,
        delivery_day_fri: !!values.delivery_day_fri,
        delivery_day_sat: !!values.delivery_day_sat,
        delivery_day_sun: !!values.delivery_day_sun,
        delivery_day_holiday: !!values.delivery_day_holiday,
        price_type: values.price_type === undefined || values.price_type === null || values.price_type === ''
          ? null
          : String(values.price_type).trim(),
        payment_term: values.payment_term === undefined || values.payment_term === null || values.payment_term === ''
          ? null
          : String(values.payment_term).trim(),
        remark: values.remark === undefined || values.remark === null || values.remark === ''
          ? null
          : String(values.remark)
      };
      const fullFormValues = form.getFieldsValue(true);
      console.log('[CommerceCustomers] form.getFieldsValue(true):', fullFormValues);
      console.log('[CommerceCustomers] save payload object:', payload);
      console.log('[CommerceCustomers] save payload json:', JSON.stringify(payload));
      const criticalKeys = [
        'customer_no',
        'name_1',
        'billing_address_cn_1',
        'delivery_address_cn_1'
      ];
      console.log(
        '[CommerceCustomers] critical key snapshot:',
        criticalKeys.reduce((acc, k) => ({ ...acc, [k]: payload[k] ?? null }), {})
      );

      const response = await fetchWithAuth(url, {
        method,
        body: JSON.stringify(payload)
      });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(isEdit ? t('commerceCustomers.saved') : t('commerceCustomers.created'));
      setDrawerOpen(false);
      loadCustomers();
    } catch (err) {
      if (err?.errorFields) return;
      message.error(`${t('commerceCustomers.saveFailed')}: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  const deleteCustomer = async (id) => {
    try {
      const response = await fetchWithAuth(`/api/commerce/customers/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error(await extractResponseError(response));
      message.success(t('commerceCustomers.deleted'));
      loadCustomers();
    } catch (err) {
      message.error(`${t('commerceCustomers.deleteFailed')}: ${err.message}`);
    }
  };

  const openPwaAccounts = async (record) => {
    setPwaModalCustomer(record);
    setPwaModalAccounts([]);
    setPwaModalOpen(true);
    setPwaModalLoading(true);
    try {
      const params = new URLSearchParams({ customerId: record.id, pageSize: '100' });
      if (companyFilter) params.set('companyId', companyFilter);
      const res = await fetchWithAuth(`/api/wpa-permissions/accounts?${params.toString()}`);
      if (!res.ok) throw new Error(await extractResponseError(res));
      const data = await res.json();
      setPwaModalAccounts(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      message.error(`載入 PWA 帳號失敗: ${err.message}`);
    } finally {
      setPwaModalLoading(false);
    }
  };

  const dayLabels = useMemo(
    () => DAY_I18N_KEYS.map((k) => t(`commerceCustomers.${k}`)),
    [t]
  );

  const columns = useMemo(() => [
    { title: t('commerceCustomers.customerNo'), dataIndex: 'customer_no', key: 'customer_no', width: 120 },
    { title: t('commerceCustomers.branchNo'), dataIndex: 'branch_no', key: 'branch_no', width: 100 },
    {
      title: t('commerceCustomers.creditLimit'),
      dataIndex: 'credit_limit',
      key: 'credit_limit',
      width: 120,
      render: (v) =>
        v != null && v !== ''
          ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
          : '—'
    },
    {
      title: t('commerceCustomers.priceType'),
      dataIndex: 'price_type',
      key: 'price_type',
      width: 120,
      ellipsis: true,
      render: (v) => (v != null && v !== '' ? String(v) : '—')
    },
    {
      title: t('commerceCustomers.customerStatus'),
      dataIndex: 'customer_status',
      key: 'customer_status',
      width: 110,
      render: (v) => {
        const s = v || 'Active';
        const color = s === 'Active' ? 'success' : s === 'Suspended' ? 'error' : 'warning';
        const label =
          s === 'Active'
            ? t('commerceCustomers.statusActive')
            : s === 'Suspended'
              ? t('commerceCustomers.statusSuspended')
              : t('commerceCustomers.statusPending');
        return <Tag color={color}>{label}</Tag>;
      }
    },
    {
      title: t('commerceCustomers.customerNameCol'),
      key: 'customer_display_name',
      width: 220,
      ellipsis: true,
      render: (_, r) => {
        const nl = parseCustomerNameLocalesRoot(r.name_locales);
        const text = resolveCustomerDisplayName(r.name_1, r.name_2, r.name_3, nl, currentLanguage);
        return text || '—';
      }
    },
    { title: t('commerceCustomers.contactName'), dataIndex: 'contact_name', key: 'contact_name', width: 120 },
    { title: t('commerceCustomers.tel'), dataIndex: 'tel', key: 'tel', width: 130 },
    { title: t('commerceCustomers.whatsapp'), dataIndex: 'whatsapp_no', key: 'whatsapp_no', width: 130 },
    { title: t('commerceCustomers.salesmanName'), dataIndex: 'salesman_name', key: 'salesman_name', width: 120 },
    { title: t('commerceCustomers.salesmanCode'), dataIndex: 'salesman_code', key: 'salesman_code', width: 100 },
    {
      title: t('commerceCustomers.tabDeliverySchedule'),
      key: 'delivery_schedule',
      width: 260,
      render: (_, record) => {
        const enabled = DAY_KEYS.map((k) => !!record[k]);
        return (
          <Space size={4} wrap>
            {dayLabels.map((d, idx) => (
              <Tag
                key={DAY_KEYS[idx]}
                color={enabled[idx] ? 'green' : 'default'}
                style={{ marginInlineEnd: 0 }}
              >
                {d}
              </Tag>
            ))}
            {!!record.delivery_day_holiday && (
              <Tag color="volcano">{t('commerceCustomers.deliveryScheduleHolidayTag')}</Tag>
            )}
          </Space>
        );
      }
    },
    ...buildCommerceAuditColumns(t, userTimezoneOffset),
    {
      title: t('commerceCustomers.actions'),
      key: 'actions',
      width: 330,
      fixed: 'right',
      render: (_, record) => (
        <Space wrap={false} size={4}>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)}>{t('commerceCustomers.edit')}</Button>
          <Button size="small" icon={<CopyOutlined />} onClick={() => openCopy(record)}>
            複製
          </Button>
          <Button size="small" icon={<TeamOutlined />} onClick={() => openPwaAccounts(record)}>
            PWA 帳號
          </Button>
          <Popconfirm title={t('commerceCustomers.confirmDelete')} onConfirm={() => deleteCustomer(record.id)}>
            <Button size="small" danger icon={<DeleteOutlined />}>{t('commerceCustomers.delete')}</Button>
          </Popconfirm>
        </Space>
      )
    }
  ], [t, dayLabels, currentLanguage, userTimezoneOffset]);

  const tabItems = [
    {
      key: 'basic',
      label: t('commerceCustomers.tabBasic'),
      forceRender: true,
      children: (
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item name="customer_no" label={t('commerceCustomers.customerNo')} rules={[{ required: true, message: t('commerceCustomers.required') }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="branch_no" label={t('commerceCustomers.branchNo')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="payment_term" label={t('commerceCustomers.paymentTerm')}>
              <AutoComplete
                allowClear
                placeholder={t('commerceCustomers.paymentTermPlaceholder')}
                options={paymentTermOptions.map(v => ({ value: v }))}
                filterOption={(input, option) =>
                  (option?.value ?? '').toLowerCase().includes(input.toLowerCase())
                }
                style={{ width: '100%' }}
              />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Text type="secondary">{t('commerceCustomers.customerNameLocalesHint')}</Text>
          </Col>
          {NAME_LINE_FORM_KEYS.map(({ line, field }) => (
            <React.Fragment key={line}>
              <Col span={24}>
                <Divider orientation="left" plain>
                  {t('commerceCustomers.nameLine')} {line}
                </Divider>
              </Col>
              <Col span={24}>
                <Form.Item
                  name={field}
                  label={
                    <span>
                      <Tooltip title="en"><Tag>EN</Tag></Tooltip>
                      {t('commerceCustomers.nameLine')} {line}
                    </span>
                  }
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['nameLocales', line, 'zh-TC']}
                  label={
                    <span>
                      <Tooltip title="zh-TC"><Tag>繁</Tag></Tooltip>
                      {t('commerceCustomers.nameLocaleZhTc')}
                    </span>
                  }
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item
                  name={['nameLocales', line, 'zh-SC']}
                  label={
                    <span>
                      <Tooltip title="zh-SC"><Tag>简</Tag></Tooltip>
                      {t('commerceCustomers.nameLocaleZhSc')}
                    </span>
                  }
                >
                  <Input />
                </Form.Item>
              </Col>
            </React.Fragment>
          ))}
          <Col span={12}>
            <Form.Item name="contact_name" label={t('commerceCustomers.contactName')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="tel" label={t('commerceCustomers.tel')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="fax" label={t('commerceCustomers.fax')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="email" label={t('commerceCustomers.email')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="whatsapp_no" label={t('commerceCustomers.whatsapp')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="credit_limit" label={t('commerceCustomers.creditLimit')}>
              <InputNumber min={0} style={{ width: '100%' }} precision={2} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="customer_status" label={t('commerceCustomers.customerStatus')} initialValue="Active">
              <Select
                options={[
                  { value: 'Active', label: t('commerceCustomers.statusActive') },
                  { value: 'Suspended', label: t('commerceCustomers.statusSuspended') },
                  { value: 'Pending', label: t('commerceCustomers.statusPending') }
                ]}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="price_type" label={t('commerceCustomers.priceType')}>
              <Input placeholder={t('commerceCustomers.priceTypePlaceholder')} allowClear />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="remark" label={t('commerceCustomers.remark')}>
              <Input.TextArea rows={3} placeholder={t('commerceCustomers.remarkPlaceholder')} allowClear />
            </Form.Item>
          </Col>
        </Row>
      )
    },
    {
      key: 'address',
      label: t('commerceCustomers.tabAddress'),
      forceRender: true,
      children: (
        <Row gutter={16}>
          <Col span={12}>
            <Title level={5}>{t('commerceCustomers.billingAddressEn')}</Title>
            <Form.Item name="billing_address_en_1" label={t('commerceCustomers.addressLine') + ' 1'}>
              <Input />
            </Form.Item>
            <Form.Item name="billing_address_en_2" label={t('commerceCustomers.addressLine') + ' 2'}>
              <Input />
            </Form.Item>
            <Form.Item name="billing_address_en_3" label={t('commerceCustomers.addressLine') + ' 3'}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Title level={5}>{t('commerceCustomers.billingAddressCn')}</Title>
            <Form.Item name="billing_address_cn_1" label={t('commerceCustomers.addressLine') + ' 1'}>
              <Input />
            </Form.Item>
            <Form.Item name="billing_address_cn_2" label={t('commerceCustomers.addressLine') + ' 2'}>
              <Input />
            </Form.Item>
            <Form.Item name="billing_address_cn_3" label={t('commerceCustomers.addressLine') + ' 3'}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Title level={5}>{t('commerceCustomers.deliveryAddressEn')}</Title>
            <Form.Item name="delivery_address_en_1" label={t('commerceCustomers.addressLine') + ' 1'}>
              <Input />
            </Form.Item>
            <Form.Item name="delivery_address_en_2" label={t('commerceCustomers.addressLine') + ' 2'}>
              <Input />
            </Form.Item>
            <Form.Item name="delivery_address_en_3" label={t('commerceCustomers.addressLine') + ' 3'}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Title level={5}>{t('commerceCustomers.deliveryAddressCn')}</Title>
            <Form.Item name="delivery_address_cn_1" label={t('commerceCustomers.addressLine') + ' 1'}>
              <Input />
            </Form.Item>
            <Form.Item name="delivery_address_cn_2" label={t('commerceCustomers.addressLine') + ' 2'}>
              <Input />
            </Form.Item>
            <Form.Item name="delivery_address_cn_3" label={t('commerceCustomers.addressLine') + ' 3'}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      )
    },
    {
      key: 'delivery',
      label: t('commerceCustomers.tabDeliverySchedule'),
      forceRender: true,
      children: (
        <div>
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            {t('commerceCustomers.deliveryScheduleHint')}
          </Text>
          <Card size="small" style={{ maxWidth: 520 }}>
            <div style={{ display: 'flex', gap: 0 }}>
              {DAY_KEYS.map((key, idx) => (
                <div
                  key={key}
                  style={{
                    flex: 1,
                    textAlign: 'center',
                    padding: '12px 4px',
                    borderRight: idx < 6 ? '1px solid #f0f0f0' : 'none',
                    background: idx >= 5 ? '#fff7e6' : undefined
                  }}
                >
                  <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 15 }}>
                    {dayLabels[idx]}
                  </div>
                  <Form.Item name={key} valuePropName="checked" style={{ marginBottom: 0 }}>
                    <Checkbox />
                  </Form.Item>
                </div>
              ))}
            </div>
            <div style={{ borderTop: '1px solid #f0f0f0', padding: '12px 8px', marginTop: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Text>{t('commerceCustomers.holidayDelivery')}</Text>
              <Form.Item name="delivery_day_holiday" valuePropName="checked" style={{ marginBottom: 0 }}>
                <Switch />
              </Form.Item>
            </div>
          </Card>
        </div>
      )
    },
    {
      key: 'salesman',
      label: t('commerceCustomers.tabSalesman'),
      forceRender: true,
      children: (
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="salesman_name" label={t('commerceCustomers.salesmanName')}>
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="salesman_code" label={t('commerceCustomers.salesmanCode')}>
              <Input maxLength={50} placeholder={t('commerceCustomers.salesmanCodePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item name="salesman_whatsapp" label={t('commerceCustomers.salesmanWhatsapp')}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      )
    }
  ];

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size={16}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Space>
              <Button icon={<ReloadOutlined />} onClick={loadCustomers}>{t('commerceCustomers.refresh')}</Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>{t('commerceCustomers.addCustomer')}</Button>
            </Space>
            <div style={{ textAlign: 'right' }}>
              <Title level={3} style={{ margin: 0 }}>{t('commerceCustomers.title')}</Title>
              <Text type="secondary">{t('commerceCustomers.subtitle')}</Text>
            </div>
          </div>

          <Space wrap>
            <Input.Search
              placeholder={t('commerceCustomers.searchPlaceholder')}
              allowClear
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onSearch={(v) => {
                setKeyword((v ?? '').trim());
                setSearchInput((v ?? '').trim());
                setListPage(1);
              }}
              style={{ width: 320 }}
              prefix={<SearchOutlined />}
              enterButton
            />
            {isTenantAdmin && (
              <Select
                allowClear
                placeholder={t('commerceCustomers.filterByCompany')}
                style={{ width: 260 }}
                value={companyFilter}
                onChange={(v) => {
                  setCompanyFilter(v);
                  setListPage(1);
                }}
                options={companiesForSelect.map(c => ({ label: c.name, value: c.id }))}
              />
            )}
          </Space>

          <Table
            rowKey="id"
            loading={loading}
            columns={columns}
            dataSource={customers}
            pagination={commerceServerTablePagination({
              page: listPage,
              pageSize: listPageSize,
              total: customersTotal,
              onChange: (p, ps) => {
                setListPage(p);
                setListPageSize(ps);
              }
            })}
            scroll={{ x: 1200, y: 'calc(100vh - 340px)' }}
          />
        </Space>
      </Card>

      <Drawer
        open={drawerOpen}
        title={editingRecord ? t('commerceCustomers.editCustomer') : t('commerceCustomers.addCustomer')}
        onClose={() => setDrawerOpen(false)}
        width="90%"
        extra={
          <Space>
            <Button onClick={() => setDrawerOpen(false)}>{t('commerceCustomers.cancel')}</Button>
            <Button type="primary" loading={saving} onClick={saveCustomer}>{t('commerceCustomers.save')}</Button>
          </Space>
        }
      >
        <Form form={form} layout="vertical">
          <Tabs
            activeKey={activeTab}
            onChange={setActiveTab}
            destroyInactiveTabPane={false}
            items={tabItems}
          />
        </Form>
      </Drawer>

      <Modal
        open={pwaModalOpen}
        title={
          <Space>
            <TeamOutlined />
            <span>
              PWA 登入帳號
              {pwaModalCustomer && (
                <Text type="secondary" style={{ fontWeight: 'normal', marginLeft: 8, fontSize: 13 }}>
                  — {pwaModalCustomer.name_1 || pwaModalCustomer.customer_no}
                </Text>
              )}
            </span>
          </Space>
        }
        onCancel={() => setPwaModalOpen(false)}
        footer={<Button onClick={() => setPwaModalOpen(false)}>關閉</Button>}
        width={780}
        destroyOnClose
      >
        <Spin spinning={pwaModalLoading}>
          {!pwaModalLoading && pwaModalAccounts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#999' }}>此客戶尚未關聯任何 PWA 帳號</div>
          ) : (
            <Table
              rowKey="id"
              size="small"
              dataSource={pwaModalAccounts}
              pagination={false}
              columns={[
                { title: '登入帳號', dataIndex: 'loginId', key: 'loginId', width: 160 },
                { title: '顯示名稱', dataIndex: 'displayName', key: 'displayName', width: 130, render: (v) => v || '—' },
                { title: '電話', dataIndex: 'phoneNo', key: 'phoneNo', width: 110, render: (v) => v || '—' },
                { title: '電郵', dataIndex: 'email', key: 'email', ellipsis: true, render: (v) => v || '—' },
                {
                  title: '有效角色',
                  dataIndex: 'effectiveRole',
                  key: 'effectiveRole',
                  width: 130,
                  render: (v, r) => v ? (
                    <Tag color={r.roleSource === 'override' ? 'blue' : 'geekblue'}>{v}</Tag>
                  ) : '—'
                },
                {
                  title: '狀態',
                  dataIndex: 'status',
                  key: 'status',
                  width: 80,
                  render: (v) => (
                    <Tag color={v === 'active' ? 'success' : 'default'}>{v}</Tag>
                  )
                }
              ]}
            />
          )}
        </Spin>
      </Modal>
    </div>
  );
};

export default CommerceCustomersPage;
